import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_BASE from '../utils/apiUrl';

const API = API_BASE;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Charger le token depuis localStorage au démarrage
  useEffect(() => {
    try {
      const savedToken = localStorage.getItem('auth_token');
      const savedUser = localStorage.getItem('auth_user');
      
      if (savedToken && savedUser) {
        const parsedUser = JSON.parse(savedUser);
        setToken(savedToken);
        setUser(parsedUser);
        axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      }
    } catch (error) {
      console.error("Auth load error:", error);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
    }
    setLoading(false);
  }, []);

  // Inscription
  const register = useCallback(async (email, password, name) => {
    try {
      const response = await axios.post(`${API}/api/auth/register`, {
        email,
        password,
        name
      });
      
      const { access_token, user: userData } = response.data;
      
      // Sauvegarder
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      
      // Mettre à jour l'état
      setToken(access_token);
      setUser(userData);
      
      // Configurer axios
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'inscription';
      return { success: false, error: message };
    }
  }, []);

  // Connexion
  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API}/api/auth/login`, {
        email,
        password
      });
      
      const { access_token, user: userData } = response.data;
      
      // Sauvegarder
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      
      // Mettre à jour l'état
      setToken(access_token);
      setUser(userData);
      
      // Configurer axios
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Email ou mot de passe incorrect';
      return { success: false, error: message };
    }
  }, []);

  // Déconnexion
  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  // Vérifier si authentifié
  const isAuthenticated = !!token && !!user;

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    register,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
